import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { Preferences } from '@capacitor/preferences'
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics'
import * as Sentry from '@sentry/capacitor'

import { useApiClient } from '@/api'
import { USER_ROLES } from '@/constants/constants'
import firebaseConfig from '@/firebase-config.json'
import { ENABLE_ANALYTICS } from '@/settings'

import type { AppointmentTypeEnum, NullEnum } from './generated/api'
import { useAuthStore } from './modules/auth/store'
import { useHomeStore } from './modules/home/store'

export const NOT_PROVIDED_WARD_PATIENT = 'Nutzung ausserhalb Aufenthalt'
export const NOT_PROVIDED_WARD_PRACTITIONER = 'Practitioner ohne zugeordnete Station'

export const NOT_PROVIDED_CLINIC_PATIENT = 'Nutzung ausserhalb Aufenthalt'
export const NOT_PROVIDED_CLINIC_PRACTITIONER = 'Practitioner ohne zugeordnete Klinik'

export const NOT_PROVIDED = 'Daten nicht verfügbar'

type CUSTOM_EVENT_ALLOWED_PROPS = {
  [CUSTOM_EVENTS.MODULE_VISITED]: {
    module: string
  }
  [CUSTOM_EVENTS.APPOINTMENT_DETAILS_OPENED]: {
    appointment_type?: AppointmentTypeEnum | NullEnum | null | string
  }
  [CUSTOM_EVENTS.PUSH_NOTIFICATION_CLICKED]: {
    notification_type?: string
  }
  [CUSTOM_EVENTS.NEWS_ARTICLE_READ]: {
    news_id: number
    news_title: string
    time: number
  }
  [CUSTOM_EVENTS.NEWS_ARTICLE_OPEN]: {
    news_id: number
    news_title: string
  }
  [CUSTOM_EVENTS.INFO_ARTICLE_OPEN]: {
    info_id: number
    info_title: string
  }
  [CUSTOM_EVENTS.INFO_ARTICLE_READ]: {
    info_id: number
    info_title: string
    time: number
  }
  [CUSTOM_EVENTS.LOGIN]: object
  [CUSTOM_EVENTS.SURVEY_STARTED]: {
    survey_template: string
    survey_name: string
    ward: string
    is_patient: boolean
  }
  [CUSTOM_EVENTS.SURVEY_COMPLETED]: {
    survey_template: string
    survey_name: string
    ward: string
    is_patient: boolean
  }
  [CUSTOM_EVENTS.SURVEY_REQUESTED]: {
    survey_template: string
    survey_name: string
    ward: string
    is_patient: boolean
  }
  [CUSTOM_EVENTS.MEAL_ORDER_PLACED]: {
    meal_type: string
    meal_date: string
  }
  [CUSTOM_EVENTS.MEAL_ORDER_CHANGED]: {
    meal_type: string
    meal_date: string
  }
  [CUSTOM_EVENTS.MEAL_SKIPPED]: {
    meal_type: string
    meal_date: string
  }
  [CUSTOM_EVENTS.MEAL_UNSKIPPED]: {
    meal_type: string
    meal_date: string
  }
  [CUSTOM_EVENTS.ABSENCE_REGISTERED]: {
    absence_duration_minutes: number
    ward: string
    clinic: string
  }
  [CUSTOM_EVENTS.ABSENCE_PROLONGED]: {
    ward: string
    clinic: string
  }
  [CUSTOM_EVENTS.ABSENCE_ENDED]: {
    overdue: string
    ward: string
    clinic: string
  }
  [CUSTOM_EVENTS.ABSENCE_QR_SCANNED]: {
    scan_result: string
    scan_context: string
    ward: string
    clinic: string
  }
}

export const CUSTOM_EVENTS = {
  MODULE_VISITED: 'module_visited',
  APPOINTMENT_DETAILS_OPENED: 'appointment_details_opened',
  PUSH_NOTIFICATION_CLICKED: 'push_notification_clicked',
  NEWS_ARTICLE_READ: 'news_article_read',
  NEWS_ARTICLE_OPEN: 'news_article_open',
  INFO_ARTICLE_OPEN: 'info_article_open',
  INFO_ARTICLE_READ: 'info_article_read',
  LOGIN: 'login',
  SURVEY_COMPLETED: 'survey_completed',
  SURVEY_STARTED: 'survey_started',
  SURVEY_REQUESTED: 'survey_requested',
  MEAL_ORDER_PLACED: 'meal_order_placed',
  MEAL_ORDER_CHANGED: 'meal_order_changed',
  MEAL_SKIPPED: 'meal_skipped',
  MEAL_UNSKIPPED: 'meal_unskipped',
  ABSENCE_REGISTERED: 'absence_registered',
  ABSENCE_PROLONGED: 'absence_prolonged',
  ABSENCE_ENDED: 'absence_ended',
  ABSENCE_QR_SCANNED: 'absence_qr_scanned',
} as const

export const CUSTOM_PROPERTIES = {
  WARD: 'ward',
  CLINIC: 'clinic',
  GENDER: 'gender',
  USER_TYPE: 'user_type',
}

export const ABSENCE_QR_SCAN_CONTEXT = {
  REGISTER: 'register',
  PROLONG: 'prolong',
  END: 'end',
}

export const ABSENCE_QR_SCAN_RESULT = {
  SUCCESS: 'success',
  FAILURE: 'failure',
}

// Cache for user properties to avoid repeated API calls
let userPropsCache: {
  gender: string
  ward: string
  clinic: string
  userType: string
} | null = null
let isFirebaseInitialized = false
let isSessionSetup = false // Track if user session is configured
let cachedUserId: string | null = null // Cache device ID

/**
 * Reset analytics cache and session on logout
 */
export function resetAnalyticsCache() {
  userPropsCache = null
  isSessionSetup = false
  cachedUserId = null
}

/** Resets module state between unit tests */
export function resetAnalyticsStateForTesting() {
  resetAnalyticsCache()
  isFirebaseInitialized = false
}

/**
 * Setup analytics session after successful login
 * Sets userId and user properties once per session
 * Should be called after login, not on every event
 */
export async function setupAnalyticsSession(): Promise<boolean> {
  // Early exit if analytics disabled or Firebase not initialized
  if (!ENABLE_ANALYTICS || !isFirebaseInitialized) {
    return false
  }

  const authStore = useAuthStore()
  const homeStore = useHomeStore()

  if (!authStore.user) {
    return false
  }

  // Skip if session already setup
  if (isSessionSetup && cachedUserId) {
    return true
  }

  try {
    // Get and cache device ID, set user ID once
    const { identifier: userId } = await Device.getId()
    cachedUserId = userId
    await FirebaseAnalytics.setUserId({ userId })

    // Load and set user properties once
    const props = await loadUserProps(authStore, homeStore)
    await setUserPropsInternal({
      [CUSTOM_PROPERTIES.GENDER]: props.gender,
      [CUSTOM_PROPERTIES.USER_TYPE]: props.userType,
      [CUSTOM_PROPERTIES.WARD]: props.ward,
      [CUSTOM_PROPERTIES.CLINIC]: props.clinic,
    })

    isSessionSetup = true
    return true
  } catch (error) {
    Sentry.captureException(error)
    return false
  }
}

async function checkAnalytics() {
  if (!ENABLE_ANALYTICS) {
    return false
  }

  // If Firebase wasn't initialized, analytics is disabled
  if (!isFirebaseInitialized) {
    return false
  }

  const authStore = useAuthStore()
  if (!authStore.user) {
    return false
  }

  return true
}

export async function initFirebaseAnalytics() {
  // Level 1: Check environment variable
  if (!ENABLE_ANALYTICS) {
    return
  }

  // Level 2: Check feature flag from backend
  // Use main API client - HEADERS function handles missing auth gracefully
  try {
    const apiClient = useApiClient()
    const features = await apiClient.features.featuresRetrieve()
    if (!features.analytics) {
      return
    }
  } catch {
    return
  }

  // Both conditions met - initialize Firebase
  if (Capacitor.isNativePlatform()) {
    // Native SDK is pre-configured via GoogleService-Info.plist / google-services.json
    isFirebaseInitialized = true
  } else if (!isFirebaseInitialized) {
    await FirebaseAnalytics.initializeFirebase(firebaseConfig)
    isFirebaseInitialized = true
  }

  // Enable collection immediately (both checks passed)
  await FirebaseAnalytics.setCollectionEnabled({ enabled: true })
}

/**
 * Internal function to set user properties without analytics check
 * Used by logEvent() after analytics check has already passed
 */
async function setUserPropsInternal(props: { [key: string]: string }) {
  await Promise.all(
    Object.entries(props).map(([key, value]) =>
      FirebaseAnalytics.setUserProperty({ name: key, value })
    )
  )
}

export async function setUserProps(props: { [key: string]: string }) {
  if (!ENABLE_ANALYTICS || !isFirebaseInitialized) {
    return
  }
  if (!(await checkAnalytics())) {
    return
  }
  await setUserPropsInternal(props)
}

/**
 * Load and cache user properties for analytics
 * Uses cached values when available, otherwise loads from API/Preferences in parallel
 */
async function loadUserProps(
  authStore: ReturnType<typeof useAuthStore>,
  homeStore: ReturnType<typeof useHomeStore>
): Promise<{ gender: string; ward: string; clinic: string; userType: string }> {
  // Return cached props if available
  if (userPropsCache) {
    return userPropsCache
  }

  const isPractitioner = authStore.user?.type === USER_ROLES.PRACTITIONER
  const isPatient = authStore.user?.type === USER_ROLES.PATIENT

  // Load all data in parallel
  const [genderPref, userTypePref, clinicPref, wardPref, stayData] = await Promise.all([
    authStore.user?.gender ? null : Preferences.get({ key: CUSTOM_PROPERTIES.GENDER }),
    authStore.user?.type ? null : Preferences.get({ key: CUSTOM_PROPERTIES.USER_TYPE }),
    Preferences.get({ key: CUSTOM_PROPERTIES.CLINIC }),
    Preferences.get({ key: CUSTOM_PROPERTIES.WARD }),
    isPatient ? homeStore.getMyStay() : Promise.resolve(null),
  ])

  const gender = authStore.user?.gender || genderPref?.value || NOT_PROVIDED
  const userType = authStore.user?.type || userTypePref?.value || NOT_PROVIDED

  const patientClinicName = stayData?.clinic_name
  const patientWardName = stayData?.ward_name

  const clinic =
    patientClinicName ||
    clinicPref?.value ||
    (isPractitioner ? NOT_PROVIDED_CLINIC_PRACTITIONER : NOT_PROVIDED_CLINIC_PATIENT)

  const ward =
    patientWardName ||
    wardPref?.value ||
    (isPractitioner ? NOT_PROVIDED_WARD_PRACTITIONER : NOT_PROVIDED_WARD_PATIENT)

  // Cache the props
  userPropsCache = { gender, ward, clinic, userType }

  // Save to Preferences in background (fire and forget)
  Promise.all([
    Preferences.set({ key: CUSTOM_PROPERTIES.GENDER, value: gender }),
    Preferences.set({ key: CUSTOM_PROPERTIES.USER_TYPE, value: userType }),
    Preferences.set({ key: CUSTOM_PROPERTIES.CLINIC, value: clinic }),
    Preferences.set({ key: CUSTOM_PROPERTIES.WARD, value: ward }),
  ]).catch(() => {
    // Ignore preference save errors
  })

  return userPropsCache
}

export async function logEvent<K extends keyof CUSTOM_EVENT_ALLOWED_PROPS>(
  eventName: K,
  params: CUSTOM_EVENT_ALLOWED_PROPS[K],
  userProperties?: { [key: string]: string }
) {
  // Early exit if analytics disabled
  if (!ENABLE_ANALYTICS || !isFirebaseInitialized) {
    return
  }

  // Setup session if not already setup
  if (!isSessionSetup) {
    await setupAnalyticsSession()
  }

  if (!(await checkAnalytics())) {
    return
  }

  try {
    // Override user properties if explicitly provided (for practitioner context)
    if (userProperties) {
      await setUserPropsInternal(userProperties)
    }

    // Log the event - userId and default props already set in setupAnalyticsSession
    await FirebaseAnalytics.logEvent({
      name: eventName,
      params,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}
